"use client";
import React from "react";
import {
  Grid,
  Box,
  Typography,
  FormControl,
  Radio,
  Card,
  CardContent,
  Chip,
  Button,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

/* ---------------------------------------------
   Modern SummaryRow component (clean + reusable)
---------------------------------------------- */
const SummaryRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Typography variant="body2" sx={{ color: "#475569" }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b" }}>
      {value}
    </Typography>
  </Box>
);

/* Color mapping for different plans */
const getPlanColors = (planName) => {
  const colorMap = {
    "Basic": { name: "#16a34a", price: "#16a34a" },      // Green
    "Pro": { name: "#2563eb", price: "#2563eb" },         // Blue
    "Premium": { name: "#ea580c", price: "#ea580c" },     // Orange
  };
  return colorMap[planName] || { name: "#1e293b", price: "#1e293b" };
};

/* Get display name - trim after space */
const getDisplayName = (planName) => {
  return planName.split(" ")[0];
};

/* Get frequency label */
const getFrequencyLabel = (plan) => {
  if (!plan.validity_days) return "month"; // Default
  const days = parseInt(plan.validity_days);
  if (days === 7) return "week";
  if (days === 1) return "day";
  if (days === 365) return "year";
  if (days < 30) return `${days} days`;
  return "month";
};

/* ---------------------------------------------
   Inner Plan Card - Enhanced with tokens and colors
---------------------------------------------- */
const innerCardContent = ({ plan, selectedPlan, isCurrentPlan }) => {
  const displayName = getDisplayName(plan.plan_name);
  const colors = getPlanColors(displayName);
  const freq = getFrequencyLabel(plan);

  return (
    <CardContent
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, flex: 1 }}>
          <Radio
            checked={selectedPlan === plan.plan_name}
            value={plan.plan_name}
            sx={{ mt: 0.5 }}
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.name }}>
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {plan.tokens?.toLocaleString()} credits/{freq}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: "right" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: colors.price, fontSize: "18px" }}>
            ${plan.plan_amount}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            /{freq}
          </Typography>
        </Box>
      </Box>

      {isCurrentPlan && (
        <Chip
          label="✓ Current Plan"
          size="small"
          sx={{
            backgroundColor: "#dcfce7",
            color: "#166534",
            fontWeight: 600,
            alignSelf: "flex-start",
          }}
        />
      )}
    </CardContent>
  );
};

/* ---------------------------------------------
   Main Component
---------------------------------------------- */
const SubscriptionTypeClean = ({
  numLocations = 1,
  selectedPlan,
  setSelectedPlanExternal,
  enableFreeTrial,
  setEnableFreeTrial,
  plansData = [],
  isEdit = false,
  onEditLocations,
  currentPlanData,
  subscriptionStatus,
  nextDueDate,

  // Token mode only
  isTokenMode = false,
  selectedTokenPack = null,

  // Unpaid invoice handling
  unpaidInvoice = null,
  onPayPendingInvoice = null,

  // Credit data (for edit mode)
  subscriptionCreditsGranted = 0,
  subscriptionCreditsConsumed = 0,
  topupCreditsGranted = 0,
  topupCreditsConsumed = 0,

  // Cancellation
  cancelAtPeriodEnd = false,
  onCancelSubscription = null,
  isCancelling = false,
}) => {
  /* ------------------------------------------------------------
     TOKEN MODE (unchanged)
  ------------------------------------------------------------ */
  if (isTokenMode) {
    return (
      <Box sx={{ width: "100%", mt: 1 }}>
        <Box
          sx={{
            p: 3,
            backgroundColor: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: "7px",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: "14px",
              mb: 2,
              color: "black",
            }}
          >
            Token Purchase Summary
          </Typography>

          {!selectedTokenPack ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              Select a token pack to see summary →
            </Typography>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" fontWeight={700}>
                  ${selectedTokenPack.amount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One-Time Payment
                </Typography>
              </Box>

              <Box>
                <SummaryRow label="Selected Pack" value={selectedTokenPack.title} />
                <SummaryRow label="Total Tokens" value={selectedTokenPack.tokens} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <Typography variant="body1" fontWeight={700}>
                    Total Payable
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>
                    ${selectedTokenPack.amount}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  }

  /* ------------------------------------------------------------
     REGULAR SUBSCRIPTION MODE
  ------------------------------------------------------------ */
  const selectedPlanObject =
    plansData?.find((p) => p.plan_name === selectedPlan) || null;

  const selectedDisplayName = selectedPlanObject ? getDisplayName(selectedPlanObject.plan_name) : "";
  const selectedColors = selectedPlanObject ? getPlanColors(selectedDisplayName) : {};

  return (
    <Box sx={{ width: "100%", mt: 3 }}>
      <Typography variant="h3" sx={{ fontSize: "18px", fontWeight: 700, mb: 2 }}>
        Choose Your Subscription
      </Typography>

      {/* Current Plan Section */}
      {isEdit && currentPlanData && (
        <Box
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Current Subscription
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentPlanData?.plan_name} — ${currentPlanData?.plan_amount}/mo
              </Typography>
            </Box>

            {subscriptionStatus && (
              <Chip
                label={
                  subscriptionStatus.charAt(0).toUpperCase() +
                  subscriptionStatus.slice(1)
                }
                size="small"
                color={
                  subscriptionStatus === "active"
                    ? "success"
                    : subscriptionStatus === "trialing"
                      ? "info"
                      : subscriptionStatus === "past_due"
                        ? "warning"
                        : "default"
                }
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>

          {/* Locations Box */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "#fff",
              borderRadius: "6px",
              border: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Current Locations
              </Typography>
              <Typography variant="caption">{numLocations} total</Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEditLocations}
              sx={{ textTransform: "none" }}
            >
              Edit Locations
            </Button>
          </Box>

          {/* Cancellation section */}
          {subscriptionStatus === 'active' && !cancelAtPeriodEnd && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="text"
                color="error"
                size="small"
                onClick={onCancelSubscription}
                disabled={isCancelling}
                sx={{ 
                  textTransform: "none", 
                  fontSize: "12px",
                  fontWeight: 600,
                  "&:hover": { backgroundColor: "rgba(220, 38, 38, 0.05)" }
                }}
              >
                {isCancelling ? "Cancelling..." : "Cancel Subscription"}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Cancellation Pending Alert */}
      {isEdit && cancelAtPeriodEnd && (
        <Box
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "#fff7ed", // Orange 50
            borderRadius: "8px",
            border: "1px solid #fdba74", // Orange 300
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: "#9a3412", fontWeight: 700 }}>
              Cancellation Pending
            </Typography>
            <Typography variant="caption" sx={{ color: "#c2410c" }}>
              Your subscription will remain active until the next billing date. You can still use your remaining credits. 
              {nextDueDate && ` Service ends on ${new Date(nextDueDate).toLocaleDateString()}.`}
            </Typography>
          </Box>
          <Chip 
            label="Pending Cancellation" 
            size="small" 
            sx={{ backgroundColor: "#ffedd5", color: "#9a3412", fontWeight: 700, fontSize: "10px" }} 
          />
        </Box>
      )}

      {/* Unpaid Invoice Section */}
      {isEdit && unpaidInvoice && subscriptionStatus === 'pending' && (
        <Box
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: "#fef2f2",
            borderRadius: "8px",
            border: "1px solid #fecaca",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ color: "#7f1d1d" }}>
                Payment Pending
              </Typography>
              <Typography variant="caption" sx={{ color: "#991b1b" }}>
                Complete payment to activate your subscription
              </Typography>
            </Box>

            <Chip
              label="Pending"
              size="small"
              sx={{
                backgroundColor: "#fee2e2",
                color: "#7f1d1d",
                fontWeight: 600,
              }}
            />
          </Box>

          {/* Payment Details Box */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "#fff",
              borderRadius: "6px",
              border: "1px solid #fecaca",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Amount Due
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b" }}>
                ${unpaidInvoice.amount?.toFixed(2) || "0.00"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Plan
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b" }}>
                {unpaidInvoice.description || "Subscription"}
              </Typography>
            </Box>
          </Box>

          {/* Pay Now Button */}
          <Button
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: "#dc2626",
              color: "#fff",
              fontWeight: 600,
              py: 1.2,
              "&:hover": {
                backgroundColor: "#b91c1c",
              },
            }}
            onClick={() => onPayPendingInvoice && onPayPendingInvoice()}
          >
            Pay Now
          </Button>
        </Box>
      )}

      {/* Main Layout Grid */}
      <Grid container columnSpacing={2}>
        {/* ---- PLAN SELECTOR ---- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            {plansData.map((plan) => {
              const isCurrentPlan =
                isEdit &&
                currentPlanData &&
                plan.plan_name === currentPlanData.plan_name;

              return (
                <Card
                  key={plan.plan_id}
                  onClick={() => setSelectedPlanExternal(plan)}
                  sx={{
                    mb: 2,
                    border:
                      selectedPlan === plan.plan_name
                        ? "2px solid #3b82f6"
                        : "1px solid #e5e7eb",
                    borderRadius: "10px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor: selectedPlan === plan.plan_name ? "#f0f9ff" : "#ffffff",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      borderColor: "#3b82f6",
                    },
                  }}
                >
                  {innerCardContent({
                    plan,
                    selectedPlan,
                    isCurrentPlan,
                  })}
                </Card>
              );
            })}

            {/* Divider before Enterprise */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, my: 3 }}>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
              <Typography sx={{ color: "#9ca3af", fontSize: "12px", fontWeight: 600, textTransform: "uppercase" }}>
                OR
              </Typography>
              <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e5e7eb" }} />
            </Box>

            {/* Enterprise Plan Section Label */}
            <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", mb: 2, letterSpacing: "0.5px" }}>
              Enterprise Plan
            </Typography>

            {/* Enterprise Plan Card */}
            <Card
              sx={{
                mb: 2,
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                cursor: "pointer",
                transition: "all 0.2s ease",
                backgroundColor: "#ffffff",
                "&:hover": {
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  borderColor: "#3b82f6",
                },
              }}
            >
              <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
                      Enterprise Plan
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Bespoke credits limits
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="body2" sx={{ color: "#64748b", lineHeight: 1.5 }}>
                  Enterprise plans including dedicated support, and volume discounts.
                </Typography>

                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#1e293b",
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "6px",
                    mt: 1,
                    "&:hover": {
                      backgroundColor: "#0f172a",
                    },
                  }}
                  href="https://askbud-i.com/contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contact Us
                </Button>
              </CardContent>
            </Card>
          </FormControl>
        </Grid>

        {/* ---- MODERN SUBSCRIPTION SUMMARY ---- */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box
            sx={{
              p: 3,
              background: "linear-gradient(to bottom right, #ffffff, #f8fafc)",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              transition: "0.2s ease",
              "&:hover": {
                boxShadow: "0 4px 14px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={700}
              mb={2}
              sx={{
                color: "#1e293b",
                letterSpacing: "0.3px",
                fontSize: "14px",
                textTransform: "uppercase",
              }}
            >
              Subscription Summary
            </Typography>

            {/* Summary Rows */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Selected Plan
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: selectedColors.name || "#1e293b" }}>
                  {selectedDisplayName || "-"}
                </Typography>
              </Box>
              <SummaryRow
                label="Number of Locations"
                value={numLocations}
              />
              <SummaryRow
                label={`Credits / ${getFrequencyLabel(selectedPlanObject || {})}`}
                value={selectedPlanObject?.tokens ? `${(selectedPlanObject.tokens * numLocations).toLocaleString()} (${selectedPlanObject.tokens.toLocaleString()} × ${numLocations})` : "-"}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ color: "#475569" }}>
                  Recurring Billing Amount
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ color: selectedColors.price || "#1e293b" }}>
                  {selectedPlanObject ? `$${(parseFloat(selectedPlanObject.plan_amount) * numLocations).toFixed(2)}` : "-"}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: -0.5,
                }}
              >
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  (${selectedPlanObject?.plan_amount}/{getFrequencyLabel(selectedPlanObject || {})} × {numLocations} locations)
                </Typography>
              </Box>

              {isEdit && nextDueDate && (
                <SummaryRow
                  label="Next Billing Date"
                  value={new Date(nextDueDate).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                />
              )}

              {isEdit && subscriptionStatus && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#475569" }}>
                    Status
                  </Typography>

                  <Chip
                    label={
                      subscriptionStatus.charAt(0).toUpperCase() +
                      subscriptionStatus.slice(1)
                    }
                    size="small"
                    color={
                      subscriptionStatus === "active"
                        ? "success"
                        : subscriptionStatus === "trialing"
                          ? "info"
                          : subscriptionStatus === "past_due"
                            ? "warning"
                            : "default"
                    }
                    sx={{
                      fontWeight: 600,
                      borderRadius: "6px",
                      height: 24,
                    }}
                  />
                </Box>
              )}

              {/* Credit Info (Edit Mode Only) */}
              {isEdit && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#475569" }}>
                      Subscription Credits
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b" }}>
                      {(subscriptionCreditsGranted - subscriptionCreditsConsumed).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                      {subscriptionCreditsGranted.toLocaleString()} granted - {subscriptionCreditsConsumed.toLocaleString()} used
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#475569" }}>
                      Topup Credits
                    </Typography>
                    <Typography variant="body2" fontWeight={600} sx={{ color: "#1e293b" }}>
                      {(topupCreditsGranted - topupCreditsConsumed).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                      {topupCreditsGranted.toLocaleString()} granted - {topupCreditsConsumed.toLocaleString()} used
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#475569", fontWeight: 600 }}>
                      Total Remaining
                    </Typography>
                    <Typography variant="body2" fontWeight={700} sx={{ color: "#3b82f6" }}>
                      {((subscriptionCreditsGranted - subscriptionCreditsConsumed) + (topupCreditsGranted - topupCreditsConsumed)).toLocaleString()}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>

            {/* Proration */}
            {selectedPlanObject &&
              Number(selectedPlanObject.prorationAmount) > 0 &&
              selectedPlan !== currentPlanData?.plan_name && (
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#fff7e6",
                    border: "1px solid #ffcc80",
                    boxShadow: "0 1px 4px rgba(255,165,0,0.15)",
                  }}
                >
                  <Typography
                    fontSize="14px"
                    fontWeight={600}
                    color="warning.dark"
                  >
                    Proration Due Today: $
                    {selectedPlanObject.prorationAmount}
                  </Typography>
                </Box>
              )}

            {/* Free Trial Checkbox - Only for Basic Plan in CREATE mode */}
            {!isEdit && selectedPlanObject?.plan_name === "Basic" && (
              <Box sx={{ mt: 2.5, mb: 2, p: 2, backgroundColor: "#eff6ff", borderRadius: "8px", border: "1px solid #bfdbfe" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={enableFreeTrial}
                      onChange={(e) => setEnableFreeTrial(e.target.checked)}
                      sx={{
                        color: "#3b82f6",
                        "&.Mui-checked": {
                          color: "#3b82f6",
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: "#1e293b" }}>
                      Enable 14 days free trial. Start paying after the trial period ends.
                    </Typography>
                  }
                />
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SubscriptionTypeClean;
